import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon, SparklesIcon } from '@heroicons/react/24/outline';

import { logError } from 'services/logging/logging';
import { GenericError } from 'support/helpers/errors/errors';
import { PaymentReconciliationDetailsData } from 'pages/PaymentReconciliation/PaymentReconciliationDetails/types';
import { Badge, BadgeColor } from 'components/Display/Badge/Badge';

type PaymentReconciliationLineItem = PaymentReconciliationDetailsData['data']['line_items'][number];

type PaymentReconciliationLineItemsStatusBadgeConfig = {
  color: BadgeColor;
  label: string;
  tooltip: string;
  icon?: React.ReactNode;
};

export const PaymentReconciliationLineItemsStatusBadge = ({ item }: { item: PaymentReconciliationLineItem }) => {
  const { t } = useTranslation();

  useEffect(() => {
    const validStatuses = [
      'RECONCILED',
      'APPROVED',
      'PENDING_REVIEW',
      'MISSING',
      'BLOCKED',
      'DISCREPANCY',
      'PENDING',
      'DROPPED',
    ];

    if (!validStatuses.includes(item.status)) {
      logError(new GenericError('Payment Verification: Unknown status.', item));
    }
  }, [item]);

  const getStatusConfig = (
    status: PaymentReconciliationLineItem['status'],
  ): PaymentReconciliationLineItemsStatusBadgeConfig | null => {
    switch (status) {
      case 'PENDING':
        return {
          color: BadgeColor.yellow,
          label: t('paymentReconciliation:status.labels.pending'),
          tooltip: t('paymentReconciliation:status.tooltips.pending'),
        };

      case 'BLOCKED':
        return {
          color: BadgeColor.red,
          label: t('paymentReconciliation:status.labels.blocked'),
          tooltip: t('paymentReconciliation:status.tooltips.blocked'),
        };
      case 'RECONCILED':
        return {
          color: BadgeColor.green,
          label: t('paymentReconciliation:status.labels.reconciled'),
          tooltip: t('paymentReconciliation:status.tooltips.reconciled'),
          icon: <SparklesIcon className="size-3 fill-green-800" />,
        };
      case 'APPROVED':
        return {
          color: BadgeColor.green,
          label: t('paymentReconciliation:status.labels.approved'),
          tooltip: t('paymentReconciliation:status.tooltips.approved'),
          icon: <CheckIcon className="size-3" />,
        };
      case 'PENDING_REVIEW':
        return {
          color: BadgeColor.blue,
          label: t('paymentReconciliation:status.labels.pendingReview'),
          tooltip: t('paymentReconciliation:status.tooltips.pendingReview'),
        };
      case 'MISSING':
        return {
          color: BadgeColor.yellow,
          label: t('paymentReconciliation:status.labels.missing'),
          tooltip: t('paymentReconciliation:status.tooltips.missing'),
        };
      case 'DROPPED':
        return {
          color: BadgeColor.gray,
          label: t('paymentReconciliation:status.labels.dropped'),
          tooltip: t('paymentReconciliation:status.tooltips.dropped'),
        };
      case 'DISCREPANCY':
        return {
          color: BadgeColor.red,
          label: t('paymentReconciliation:status.labels.discrepancy'),
          tooltip: t('paymentReconciliation:status.tooltips.discrepancy'),
        };
      default:
        return null;
    }
  };

  const config = getStatusConfig(item.status);

  if (!config) {
    return null;
  }

  return (
    <Badge color={config.color} tooltip={config.tooltip}>
      <span className="inline-flex items-center gap-1">
        {config.label}
        {config.icon}
      </span>
    </Badge>
  );
};
